import { Trans, useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';

import { ErrorAlert, InfoAlert, WarningAlert } from '../../components/Alert.tsx';
import { Space } from '../../data/models.ts';
import { spaceDetailQuery, suggestionPaginatedListQuery } from '../../data/queries.ts';
import useAllPagesQuery from '../../utils/useAllPagesQuery.tsx';

export const useSpace = () => {
    const { data: space } = useQuery({ ...spaceDetailQuery(), staleTime: Infinity });
    return space as Space;
};

interface LimitAlertOptions {
    limit?: number;
    totalCount?: number;
    limitTranslationKey?: string;
    limitReachedTranslationKey?: string;
    displayThreshold?: number;
}

const isLimitReached = ({ totalCount, limit }: LimitAlertOptions) =>
    totalCount != null && limit != null && totalCount >= limit;

const LimitAlert = (options: LimitAlertOptions) => {
    const { t } = useTranslation('admin');
    const { limit, totalCount, limitTranslationKey, limitReachedTranslationKey, displayThreshold = 0.8 } = options;
    if (totalCount == null || limit == null) return null;
    const reached = isLimitReached(options);
    const relativeProgress = limit === 0 ? 1 : totalCount / limit;
    if (!reached && relativeProgress < displayThreshold) return null;

    const Comp = reached ? ErrorAlert : relativeProgress >= 0.8 ? WarningAlert : InfoAlert;
    return (
        <Comp>
            <Trans
                t={t}
                // Translation keys are supplied by the owning feature.
                // @ts-ignore
                i18nKey={reached && limitReachedTranslationKey ? limitReachedTranslationKey : limitTranslationKey}
                values={{ limit, totalCount }}
            />
        </Comp>
    );
};

export const useLimitAlert = (options: LimitAlertOptions) => ({
    isLimitReached: isLimitReached(options),
    limitAlert: <LimitAlert {...options} />,
});

export const useSuggestionsQuery = (fieldNames: string[]) =>
    useAllPagesQuery({
        ...suggestionPaginatedListQuery(
            { fieldName: [...new Set(fieldNames)].sort() },
            {
                translatable: true,
                pageSize: 500,
            }
        ),
        enabled: fieldNames.length > 0,
        staleTime: 5 * 60 * 1000,
    });

export const useSuggestions = (fieldName: string) => useSuggestionsQuery([fieldName]).data;
